import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { animate, motion, useMotionValue, type PanInfo } from 'framer-motion'
import { Button } from '@structural-climbing/ui'
import type { GymContext } from '../layouts/GymLayout'
import { useGymBranding } from '../features/gym/useGymBranding'
import { useGymOccupancy } from '../features/gym/useGymOccupancy'
import { useMyMemberships } from '../features/gym/useMyMemberships'
import { useMyGyms } from '../features/gym/useMyGyms'
import { getTodayKey, isGymOpen } from '../features/gym/gymHours'
import { GymCover } from '../features/gym/GymCover'
import { WorkingHoursModal } from '../features/gym/WorkingHoursModal'
import { GymMapModal } from '../features/gym/GymMapModal'

/** How far, or how fast, a swipe to the right counts as "go back". */
export function shouldSwipeBack(offsetX: number, velocityX: number): boolean {
  return offsetX > 110 || velocityX > 600
}

/**
 * The gym's own page: its cover, logo, description, hours, map and address,
 * and the other gyms you climb at.
 *
 * Opened from "Detalii" in "Sălile tale". It slides in from the right like a
 * pushed screen and goes back the same way, by the back button or a swipe to
 * the right, since an installed app on iOS has no browser gesture to do it.
 */
export function GymDetailsPage() {
  const { gymId, gymSlug } = useOutletContext<GymContext>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const branding = useGymBranding(gymId, apiUrl)
  const occupancy = useGymOccupancy(gymId, apiUrl)
  const { childAccount } = useMyMemberships()
  // One entry per gym, however many plans the member has held there.
  const { gyms } = useMyGyms()
  const [showHours, setShowHours] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const x = useMotionValue(0)

  // Back to where the member came from; straight to Home when this page was
  // opened directly, so the back button never leaves the app.
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) void navigate(-1)
    else void navigate(`/${gymSlug}/dashboard`, { replace: true })
  }
  const slideOutAndBack = () => {
    void animate(x, window.innerWidth, { duration: 0.18, ease: 'easeIn' }).then(goBack)
  }
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (shouldSwipeBack(info.offset.x, info.velocity.x)) slideOutAndBack()
    else void animate(x, 0, { type: 'spring', stiffness: 500, damping: 40 })
  }

  const others = gyms.filter((g) => g.slug !== gymSlug)
  const isChild = childAccount !== null
  const hasHours = Boolean(branding?.working_hours && branding.timezone)
  const open = hasHours && isGymOpen(branding!.working_hours, branding!.timezone)
  const today = hasHours ? branding!.working_hours![getTodayKey(branding!.timezone!)] : null
  const hasMap = Boolean(branding?.map_url && branding.map_file_type)
  const initials = (branding?.name ?? '')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  return (
    <motion.div
      data-testid="gym-details"
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      transition={{ type: 'spring', stiffness: 420, damping: 42 }}
      style={{ x, touchAction: 'pan-y' }}
      drag="x"
      dragDirectionLock
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={{ left: 0, right: 0.9 }}
      onDragEnd={onDragEnd}
      className="flex min-h-full flex-1 flex-col bg-[var(--gym-page)] pb-8 shadow-e3"
    >
      <GymCover
        name={branding?.name ?? ''}
        coverUrl={branding?.cover_url}
        color={branding?.primary_color}
        className="h-56 shrink-0 pt-[var(--safe-t)] md:rounded-b-lg"
      >
        <Button
          isIconOnly
          radius="full"
          aria-label={t('gymSpace.back')}
          onPress={slideOutAndBack}
          className="absolute left-3 top-[calc(var(--safe-t)+0.75rem)] z-10 bg-white/90 text-lg font-bold text-foreground shadow-e2"
        >
          ‹
        </Button>
      </GymCover>

      {branding && (
        <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4">
          {/* relative z-10: the cover's photo is positioned, so an unpositioned
              logo pulled up over it was painted underneath. */}
          <span className="relative z-10 -mt-10 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-4 border-[var(--gym-page)] bg-content2 text-xl font-bold shadow-e2 ring-2 ring-[var(--gym-primary)]">
            {branding.image_url ? (
              <img
                src={branding.image_url}
                alt={`${branding.name} logo`}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-foreground/60">{initials}</span>
            )}
          </span>

          <div>
            <h1 className="text-2xl font-bold tracking-tight">{branding.name}</h1>
            {branding.description && (
              <p className="mt-1 text-[15px] leading-relaxed text-foreground/55">
                {branding.description}
              </p>
            )}
          </div>

          {hasHours && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className={`font-semibold ${open ? 'text-success' : 'text-danger'}`}>
                ● {open ? t('gymHours.openNow') : t('gymHours.closed')}
              </span>
              {open && today && !today.closed && (
                <span className="text-sm text-foreground/50">
                  · {t('gymSpace.openUntil', { time: today.close })}
                </span>
              )}
              <Button
                variant="light"
                size="sm"
                onPress={() => setShowHours(true)}
                className="ml-auto h-auto min-w-0 px-0 font-semibold text-[var(--gym-primary)] data-[hover=true]:bg-transparent"
              >
                {t('gymSpace.allHours')} ›
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {open && occupancy > 0 && (
              <span className="flex items-center gap-1.5 rounded-full bg-content1 px-3 py-1.5 text-sm font-semibold shadow-e1">
                <span aria-hidden className="h-2 w-2 rounded-full bg-success" />
                {t('gymSpace.peopleIn', { count: occupancy })}
              </span>
            )}
            {hasMap && (
              <Button
                size="sm"
                radius="full"
                onPress={() => setShowMap(true)}
                className="bg-content1 text-sm font-semibold shadow-e1"
              >
                {t('gymSpace.map')}
              </Button>
            )}
            {branding.address && (
              <Button
                as="a"
                size="sm"
                radius="full"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${branding.name}, ${branding.address}`,
                )}`}
                target="_blank"
                rel="noreferrer"
                className="bg-content1 text-sm font-semibold shadow-e1"
              >
                📍 {branding.address}
              </Button>
            )}
          </div>

          {others.length > 0 && (
            <div className="flex flex-col gap-2 border-t border-[var(--gym-line)] pt-4">
              <p className="eyebrow text-foreground/40">{t('gymSpace.alsoAt')}</p>
              {others.map((gym) => (
                <Link
                  key={gym.id}
                  to={isChild ? `/${gym.slug}/bouldering/routes` : `/${gym.slug}/dashboard`}
                  className="flex items-center gap-3 rounded-lg border border-[var(--gym-line)] bg-content1 p-2 shadow-e1"
                >
                  <GymCover
                    name={gym.name}
                    coverUrl={gym.cover_url}
                    className="h-10 w-14 shrink-0 rounded-md"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{gym.name}</span>
                  <span className="shrink-0 pr-1 text-sm font-semibold text-[var(--gym-primary)]">
                    {t('gymSpace.switchTo')} ›
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {hasHours &&
        createPortal(
          <WorkingHoursModal
            workingHours={branding!.working_hours!}
            timezone={branding!.timezone!}
            isOpen={showHours}
            onClose={() => setShowHours(false)}
          />,
          document.body,
        )}
      {hasMap && (
        <GymMapModal
          mapUrl={branding!.map_url!}
          mapFileType={branding!.map_file_type!}
          isOpen={showMap}
          onClose={() => setShowMap(false)}
        />
      )}
    </motion.div>
  )
}
